/**
 * Material Design 3 Token System
 *
 * Google Material Design 3 (Material You) design tokens for solid, tonal surfaces.
 * Uses tonal elevation instead of glassmorphism, crisp dual-layer shadows,
 * and rounded rectangle shapes.
 *
 * @see https://m3.material.io/styles
 */

// ============================================================================
// Shape System (MD3 Shape Scale)
// ============================================================================
// MD3 uses a shape scale from None to Full, applied consistently across components.

export const materialRadius = {
  /** None — no rounding (0px) */
  xs: 0,
  /** Extra Small — small controls: chips, tags (4px) */
  sm: 4,
  /** Small — buttons, inputs, menu items (8px) */
  md: 8,
  /** Medium — cards, panels, alerts (12px) */
  lg: 12,
  /** Large — modals, large surfaces (16px) */
  xl: 16,
  /** Extra Large — dialogs, bottom sheets (28px) */
  xxl: 28,
  /** Full — FABs, capsule buttons (9999px) */
  full: 9999,
} as const;

// ============================================================================
// Tonal Surface System (replaces glassmorphism)
// ============================================================================
// MD3 uses tonal color elevation: primary color overlaid at varying opacities
// on the surface color. No blur, no specular highlights, no translucency.

export const materialSurface = {
  /**
   * Regular surface — primary surfaces (cards, panels, sidebars).
   * Uses tonal elevation with solid backgrounds.
   */
  regular: {
    light: {
      background: 'rgba(255, 255, 255, 1)',
      backgroundHover: 'rgba(103, 80, 164, 0.08)',
      border: '1px solid rgba(0, 0, 0, 0.12)',
      borderHover: '1px solid rgba(0, 0, 0, 0.16)',
      shadow: '0px 1px 2px 0px rgba(0,0,0,0.30), 0px 1px 3px 1px rgba(0,0,0,0.15)',
      shadowHover: '0px 1px 2px 0px rgba(0,0,0,0.30), 0px 2px 6px 2px rgba(0,0,0,0.15)',
    },
    dark: {
      background: 'rgba(28, 27, 31, 1)',
      backgroundHover: 'rgba(208, 188, 255, 0.08)',
      border: '1px solid rgba(255, 255, 255, 0.12)',
      borderHover: '1px solid rgba(255, 255, 255, 0.16)',
      shadow: '0px 1px 2px 0px rgba(0,0,0,0.30), 0px 1px 3px 1px rgba(0,0,0,0.15)',
      shadowHover: '0px 1px 2px 0px rgba(0,0,0,0.30), 0px 2px 6px 2px rgba(0,0,0,0.15)',
    },
  },

  /**
   * Tonal surface — subtle elevation using primary color overlay.
   * Used for selected states, chips, secondary containers.
   */
  tonal: {
    light: {
      background: 'rgba(103, 80, 164, 0.05)',
      backgroundHover: 'rgba(103, 80, 164, 0.12)',
      border: '1px solid rgba(0, 0, 0, 0.08)',
      borderHover: '1px solid rgba(0, 0, 0, 0.12)',
      shadow: 'none',
    },
    dark: {
      background: 'rgba(208, 188, 255, 0.05)',
      backgroundHover: 'rgba(208, 188, 255, 0.12)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      borderHover: '1px solid rgba(255, 255, 255, 0.12)',
      shadow: 'none',
    },
  },

  /**
   * Outlined surface — no fill, outline border only.
   * Used for outlined cards, text fields, outlined buttons.
   */
  outlined: {
    light: {
      background: 'transparent',
      backgroundHover: 'rgba(103, 80, 164, 0.04)',
      border: '1px solid rgba(0, 0, 0, 0.12)',
      borderHover: '1px solid rgba(0, 0, 0, 0.20)',
      shadow: 'none',
    },
    dark: {
      background: 'transparent',
      backgroundHover: 'rgba(208, 188, 255, 0.04)',
      border: '1px solid rgba(255, 255, 255, 0.12)',
      borderHover: '1px solid rgba(255, 255, 255, 0.20)',
      shadow: 'none',
    },
  },
} as const;

// ============================================================================
// Tonal Elevation Overlays (MD3)
// ============================================================================
// MD3 dark theme uses a primary color overlay at increasing opacity to indicate elevation.
// This replaces traditional shadows in dark mode.

export const materialTonalElevation = {
  /** Level 0: No overlay (0%) */
  0: 'transparent',
  /** Level 1: Subtle (5% primary) */
  1: 'rgba(103, 80, 164, 0.05)',
  /** Level 2: Low (8% primary) */
  2: 'rgba(103, 80, 164, 0.08)',
  /** Level 3: Medium (11% primary) */
  3: 'rgba(103, 80, 164, 0.11)',
  /** Level 4: High (12% primary) */
  4: 'rgba(103, 80, 164, 0.12)',
  /** Level 5: Maximum (14% primary) */
  5: 'rgba(103, 80, 164, 0.14)',
} as const;

// ============================================================================
// Shadow System (MD3 Elevation)
// ============================================================================
// MD3 uses crisp, dual-layer shadows (key shadow + ambient shadow).

export const materialShadows = {
  /** Level 0: No elevation */
  0: 'none',
  /** Level 1: Cards at rest, buttons */
  1: '0px 1px 2px 0px rgba(0,0,0,0.30), 0px 1px 3px 1px rgba(0,0,0,0.15)',
  /** Level 2: Hover states, raised buttons */
  2: '0px 1px 2px 0px rgba(0,0,0,0.30), 0px 2px 6px 2px rgba(0,0,0,0.15)',
  /** Level 3: Menus, dropdowns, popovers */
  3: '0px 1px 3px 0px rgba(0,0,0,0.30), 0px 4px 8px 3px rgba(0,0,0,0.15)',
  /** Level 4: Modals, dialogs */
  4: '0px 2px 3px 0px rgba(0,0,0,0.30), 0px 6px 10px 4px rgba(0,0,0,0.15)',
  /** Level 5: Navigation drawers, full overlays */
  5: '0px 4px 4px 0px rgba(0,0,0,0.30), 0px 8px 12px 6px rgba(0,0,0,0.15)',
  /** Level 6: Maximum (extended for MUI compatibility) */
  6: '0px 4px 4px 0px rgba(0,0,0,0.30), 0px 8px 12px 6px rgba(0,0,0,0.15)',
} as const;

// ============================================================================
// Spacing System (4px base grid)
// ============================================================================
// MD3 uses a 4dp base grid, same increments but different naming.

export const materialSpacing = {
  /** 4px — minimal gaps */
  xxs: 4,
  /** 8px — compact spacing */
  xs: 8,
  /** 12px — tight content spacing */
  sm: 12,
  /** 16px — standard padding */
  md: 16,
  /** 24px — section gaps */
  lg: 24,
  /** 32px — large section gaps */
  xl: 32,
  /** 48px — page-level spacing */
  xxl: 48,
} as const;

// ============================================================================
// Motion Tokens (MD3)
// ============================================================================
// MD3 uses emphasized and standard easing curves, not spring physics.

export const materialMotion = {
  /** Standard easing — most transitions */
  standard: 'cubic-bezier(0.2, 0, 0, 1)',
  /** Standard decelerate — entering elements */
  standardDecelerate: 'cubic-bezier(0, 0, 0, 1)',
  /** Standard accelerate — exiting elements */
  standardAccelerate: 'cubic-bezier(0.3, 0, 1, 1)',
  /** Emphasized — important transitions */
  emphasized: 'cubic-bezier(0.2, 0, 0, 1)',
  /** Emphasized decelerate — entering with emphasis */
  emphasizedDecelerate: 'cubic-bezier(0.05, 0.7, 0.1, 1)',
  /** Emphasized accelerate — exiting with emphasis */
  emphasizedAccelerate: 'cubic-bezier(0.3, 0, 0.8, 0.15)',

  /** Durations */
  durationShort1: 50,
  durationShort2: 100,
  durationShort3: 150,
  durationShort4: 200,
  durationMedium1: 250,
  durationMedium2: 300,
  durationMedium3: 350,
  durationMedium4: 400,
  durationLong1: 450,
  durationLong2: 500,
  durationLong3: 550,
  durationLong4: 600,
} as const;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get material surface properties for a given variant and mode.
 */
export function getMaterialSurface(isDark: boolean, variant: 'regular' | 'tonal' | 'outlined' = 'regular') {
  return isDark ? materialSurface[variant].dark : materialSurface[variant].light;
}

/**
 * Get the shadow for a given MD3 elevation level.
 */
export function getMaterialShadow(elevation: keyof typeof materialShadows = 1): string {
  return materialShadows[elevation];
}

// ============================================================================
// Type Exports
// ============================================================================

export type MaterialSurfaceVariant = 'regular' | 'tonal' | 'outlined';
export type MaterialRadiusKey = keyof typeof materialRadius;
export type MaterialSpacingKey = keyof typeof materialSpacing;
export type MaterialShadowLevel = keyof typeof materialShadows;
